import { useEffect, useState } from "react";
import { Pencil, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "@/components/ui/switch";
import { api, gbp, errMsg, imgSrc } from "@/lib/api";
import { StoreBadge } from "@/components/StoreBadge";
import ProductForm from "@/pages/admin/ProductForm";
import BulkPrice from "@/pages/admin/BulkPrice";

export default function ProductsTab() {
  const [products, setProducts] = useState([]);
  const [editing, setEditing] = useState(null);

  const load = () => api.get("/admin/products").then((r) => setProducts(r.data));
  useEffect(() => { load(); }, []);

  const save = async (data) => {
    try {
      if (editing?.id) await api.put(`/admin/products/${editing.id}`, data);
      else await api.post("/admin/products", data);
      toast.success("Product saved");
      setEditing(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggleStock = async (p) => {
    const { id, created_at, ...rest } = p;
    await api.put(`/admin/products/${id}`, { ...rest, in_stock: !p.in_stock });
    toast.success(`${p.name} marked ${p.in_stock ? "sold out" : "in stock"}`);
    load();
  };
  const remove = async (p) => {
    if (!window.confirm(`Delete ${p.name}?`)) return;
    await api.delete(`/admin/products/${p.id}`);
    toast.success("Product deleted");
    load();
  };

  return (
    <div className="mt-4">
      <button onClick={() => setEditing({})} className="flex items-center gap-2 rounded-full bg-[#8C2220] text-white px-5 py-2.5 text-sm font-semibold" data-testid="add-product-button"><Plus className="w-4 h-4" /> Add product</button>
      <BulkPrice onDone={load} />
      <ProductForm open={editing !== null} initial={editing} onClose={() => setEditing(null)} onSave={save} />
      <div className="mt-6 grid gap-3" data-testid="admin-product-list">
        {products.map((p) => (
          <div key={p.id} className="flex items-center gap-4 bg-white border border-[#EDE4D3] rounded-2xl p-3" data-testid={`admin-product-${p.id}`}>
            <img src={imgSrc(p.image_url)} alt="" className="w-14 h-14 object-contain bg-[#F7F3EB] rounded-lg p-1" />
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{p.name}</p>
              <div className="flex items-center gap-2 mt-1"><StoreBadge store={p.store} /><span className="text-xs text-slate-500">{p.category}</span></div>
            </div>
            <span className="font-semibold w-20 text-right">{gbp(p.price)}</span>
            <span className="text-xs w-16 text-right text-slate-500" data-testid={`stock-qty-${p.id}`}>{p.stock_qty == null ? "No limit" : `${p.stock_qty} left`}</span>
            <label className="flex items-center gap-2 text-xs w-28">
              <Switch checked={p.in_stock} onCheckedChange={() => toggleStock(p)} data-testid={`stock-toggle-${p.id}`} />
              {p.in_stock ? "In stock" : "Sold out"}
            </label>
            <button onClick={() => setEditing(p)} className="p-2 hover:text-[#8C2220]" data-testid={`edit-product-${p.id}`}><Pencil className="w-4 h-4" /></button>
            <button onClick={() => remove(p)} className="p-2 hover:text-rose-700" data-testid={`delete-product-${p.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
